// src/_Hooks/useGetDataRecords.js
import { useJwt } from "react-jwt";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ApiConsume } from "../_API/ApiConsume";

/**
 * Hook genérico para obtener **una** página de resultados (o un
 * array simple) desde la API.
 *
 * Parámetros:
 *   • SECTION_GET : alias de la sección (ej. "CONSORTIUMS__OWN_GET_LIST")
 *   • page        : número de página (default 1)
 *   • pageSize    : tamaño de página (default 50)
 *   • extraParams : objeto con query-params adicionales
 */
export const useGetDataRecords = (
  SECTION_GET = "",
  page = 1,
  pageSize = 50,
  extraParams = {}
) => {
  /* ─────────────── token & user ─────────────── */
  const TOKEN =
    localStorage.getItem("USER_AUTH") ||
    sessionStorage.getItem("USER_AUTH") ||
    "";

  const { decodedToken } = useJwt(TOKEN);
  const { id } = useParams();                      /* por si algún
                                                      hook lo usa */

  /* ─────────────── estado local ─────────────── */
  const [useDataRecords, setDataRecords] = useState({
    isLoading  : true,
    data       : [],
    pagination : { count: 0, next: null, previous: null },
    totals     : null,
  });

  /* ─────────────── mapa de secciones ─────────────── */
  const ENDPOINT_MAP = {
    /*   alias                      →  endpoint usado en ApiConsume   */
    CONSORTIUMS__OWN_GET_LIST              : "CONSORTIUMS__OWN_GET_LIST",
    CONSORTIUMS__CONSORTIUM_GET_LIST       : "CONSORTIUMS__CONSORTIUM_GET_LIST",
    CONSORTIUMS__PERIOD_GET_LIST           : "CONSORTIUMS__PERIOD_GET_LIST",
    FOLDERS__LOCAL_GET_LIST                : "FOLDERS__LOCAL_GET_LIST",
    MOVEMENTS__EXPENSES_SECTOR_GET_LIST    : "MOVEMENTS__EXPENSES_SECTOR_GET_LIST",
    MOVEMENTS__EXPENSES_ACCOUNT_GET_LIST   : "MOVEMENTS__EXPENSES_ACCOUNT_GET_LIST",
    MOVEMENTS__EXPENSES_PROVIDER_GET_LIST  : "MOVEMENTS__EXPENSES_PROVIDER_GET_LIST",
    CONSORTIUMS__BANK_GET_LIST             : "CONSORTIUMS__BANK_GET_LIST",
    CONSORTIUMS__CASH_GET_LIST             : "CONSORTIUMS__CASH_GET_LIST",
    PERIODS__PERIOD_GET_LIST               : "PERIODS__PERIOD_GET_LIST",
    USERS__GROUPS_GET_LIST                 : "USERS__GROUPS_GET_LIST",
    USERS__LIST_ALL_USERS                  : "USERS__LIST_ALL_USERS",
    ACCESO_TERCEROS_GET_LIST               : "ACCESO_TERCEROS_GET_LIST",
    SUPPLIERS__SUPPLIER_GET_LIST           : "SUPPLIERS__SUPPLIER_GET_LIST",
    INVESTMENTS__INVESTMENT_GET_LIST       : "INVESTMENTS__INVESTMENT_GET_LIST",
    INVESTMENTS__ACCOUNT_GET_LIST_BY_CONSORCIO : "INVESTMENTS__ACCOUNT_GET_LIST_BY_CONSORCIO",
    FOREIGNCURRENCY__FX_GET_LIST           : "FOREIGNCURRENCY__FX_GET_LIST",
    FINANCIAL_MOVEMENTS__GET_LIST          : "FINANCIAL_MOVEMENTS__GET_LIST",
    COLLECTIONS__COLLECTION_GET_LIST       : "COLLECTIONS__COLLECTION_GET_LIST",
    MOVEMENTS__COLLECTION_GET_LIST         : "MOVEMENTS__COLLECTION_GET_LIST",
    LEGALES__JUICIOS_GET_LIST              : "LEGALES__JUICIOS_GET_LIST",
    FOLDERS__LOCAL_GET_PUBLIC_LIST         : "FOLDERS__LOCAL_GET_PUBLIC_LIST",
    CONSORTIUMS__EXPENSE_CONCEPT_GET_LIST  : "CONSORTIUMS__EXPENSE_CONCEPT_GET_LIST",
    EXPENSE_CONCEPTS__LIST                 : "CONSORTIUMS__EXPENSE_CONCEPT_GET_LIST",
  };

  /* ─────────────── efecto ⇒ carga datos ─────────────── */
  useEffect(() => {
    const fetchPage = async () => {
      const isPublic = SECTION_GET === "FOLDERS__LOCAL_GET_PUBLIC_LIST";

      /* TOKEN obligatorio en endpoints privados */
      if (!TOKEN && !isPublic) {
        setDataRecords((s) => ({ ...s, isLoading: false }));
        console.error("useGetDataRecords: endpoint privado sin TOKEN");
        return;
      }

      const endpoint = ENDPOINT_MAP[SECTION_GET];
      if (!endpoint) {
        console.error(`useGetDataRecords: SECTION_GET '${SECTION_GET}' no existe`);
        setDataRecords((s) => ({ ...s, isLoading: false }));
        return;
      }

      /* ----------- query-params que viajan a la API ----------- */
      const queryParams = {
        page,
        page_size: pageSize,
        /* user_id opcional – sólo si el back lo necesita */
        ...(decodedToken?.user_id ? { user_id: decodedToken.user_id } : {}),
        ...extraParams,
      };

      try {
        const API = await ApiConsume(
          endpoint,
          "GET",
          {},
          null,
          TOKEN,
          false,
          queryParams
        );

        if (API?.status !== 200) {
          throw new Error(`status=${API?.status}`);
        }

        const payload = API.data;

        const shouldFilterConsorcios =
          SECTION_GET === "CONSORTIUMS__CONSORTIUM_GET_LIST" &&
          !queryParams?.include_inactive;

        const filterActives = (items) => {
          if (!Array.isArray(items)) return items;
          return items.filter((item) => {
            const flag = item?.activo;
            if (flag === undefined || flag === null) return true;
            if (typeof flag === "string") {
              const normalized = flag.toLowerCase();
              return normalized === "true" || normalized === "1";
            }
            return Boolean(flag);
          });
        };

        const maybeFilter = (items) =>
          shouldFilterConsorcios ? filterActives(items) : items;

        /* DRF paginado → { count, next, previous, results } */
        if (Array.isArray(payload?.results)) {
          const filteredResults = maybeFilter(payload.results);
          setDataRecords({
            isLoading : false,
            data      : filteredResults,
            pagination: {
              count    : payload.count,
              next     : payload.next,
              previous : payload.previous,
            },
            totals    : payload.totals || null,
          });
        }
        /* Array plano sin paginación */
        else if (Array.isArray(payload)) {
          const filteredArray = maybeFilter(payload);
          setDataRecords({
            isLoading : false,
            data      : filteredArray,
            pagination: { count: filteredArray.length, next: null, previous: null },
            totals    : payload.totals || null,
          });
        }
        /* Objeto (caso especial) */
        else {
          setDataRecords({
            isLoading : false,
            data      : payload,
            pagination: { count: 0, next: null, previous: null },
            totals    : payload?.totals || null,
          });
        }
      } catch (err) {
        console.error("useGetDataRecords →", err);
        setDataRecords({
          isLoading : false,
          data      : [],
          pagination: { count: 0, next: null, previous: null },
          totals    : null,
        });
      }
    };

    fetchPage();
  }, [
    SECTION_GET,
    page,
    pageSize,
    JSON.stringify(extraParams),
    decodedToken?.user_id,
    TOKEN,
  ]);

  return { useDataRecords, setDataRecords };
};
